import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, Wallet } from 'lucide-react';
import api from '../../services/api';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

export default function RevenueReportPage() {
  const [groupBy, setGroupBy] = useState('day');
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ totalRevenue: 0, totalCollected: 0 });
  const [loading, setLoading] = useState(true);
  const currency = useCompanyCurrency();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/bookkeeping/revenue-report', { params: { groupBy } })
      .then(res => {
        setRows(res.data.rows || []);
        setTotals({ totalRevenue: res.data.totalRevenue || 0, totalCollected: res.data.totalCollected || 0 });
      })
      .finally(() => setLoading(false));
  }, [groupBy]);

  useEffect(() => { load(); }, [load]);

  const sortedRows = [...rows].sort((a, b) => b.period.localeCompare(a.period));

  return (
    <div className={PAGE}>
      <PageHeader title="Revenue & Collection Report" subtitle="Revenue recognized (invoices sent) vs. cash actually collected, broken down by day or month." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><TrendingUp size={12} /> Total revenue recognized</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(totals.totalRevenue, currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><Wallet size={12} /> Total cash collected</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(totals.totalCollected, currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex items-center gap-2">
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm flex-1 focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
            <option value="day">By Day</option>
            <option value="month">By Month</option>
          </select>
          <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F3F3F3] disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E5]">
                <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Period</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Revenue Recognized</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Cash Collected</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F2F2]">
              {sortedRows.map(r => (
                <tr key={r.period}>
                  <td className="py-2.5 pr-4 font-medium text-[#2E2E2E]">{r.period}</td>
                  <td className={cn('py-2.5 pr-4 text-right', r.revenue < 0 ? 'text-[#BA0517]' : 'text-[#2E2E2E]')}>{formatCurrency(r.revenue, currency)}</td>
                  <td className={cn('py-2.5 pr-4 text-right', r.collected < 0 ? 'text-[#BA0517]' : 'text-[#2E2E2E]')}>{formatCurrency(r.collected, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && sortedRows.length === 0 && <p className="text-sm text-[#706E6B] py-8 text-center">No revenue or collection activity yet.</p>}
        </div>
      </div>
    </div>
  );
}
